import {
  Card,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  TableHead,
  TablePagination,
  FormLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  IconButton,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { formatSierraLeoneLeone, getStatusColor } from "../utils/commonFuns";
import { CloseTwoTone } from "@mui/icons-material";

interface ITableList {
  apiResponse:
    | {
        totalRecords: number;
        totalPages: number;
        list: Record<string, string>[];
      }
    | Record<string, any>;
  columns: Record<string, any>[] | [];
  onPageChange: (obj: any) => void;
  loading: any;
  pageProps: any;
  actionsRows?: any;
}

function TableList({
  columns,
  onPageChange,
  pageProps,
  actionsRows,
  apiResponse,
  loading,
}: ITableList) {
  const [sortColumn, setSortColumn] = useState<string>("");
  const { list: data, totalRecords, totalPages } = apiResponse;

  const [showMap, setShowMap] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(pageProps?.pageNumber > 0 ? pageProps?.pageNumber - 1 : 0);
    setRowsPerPage(pageProps?.perPage || 10);
    setSortDirection(pageProps?.isDescending ? "desc" : "asc");
    setSortColumn(pageProps.sortingOn ?? "");
  }, [pageProps]);

  const handleSort = (columnId: string) => {
    // Toggle sorting direction
    if (columnId === "actions") return;
    const isDescending = sortDirection === "asc" ? "desc" : "asc";
    onPageChange({
      ...pageProps,
      isDescending: isDescending === "desc",
      sortingOn: columnId,
      perPage: rowsPerPage,
      pageNumber: page + 1,
    });
    if (columnId === sortColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    onPageChange({
      ...pageProps,
      isDescending: sortDirection === "desc",
      sortingOn: sortColumn,
      perPage: rowsPerPage,
      pageNumber: newPage + 1,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(() => parseInt(event.target.value, 10));
    setPage(() => 0);
    onPageChange({
      ...pageProps,
      isDescending: sortDirection === "desc",
      sortingOn: sortColumn,
      perPage: parseInt(event.target.value, 10),
      pageNumber: page + 1,
    });
  };

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns?.map((column: Record<string, string>) => (
                <TableCell
                  key={column.id}
                  className={`text-sm capitalize whitespace-nowrap text-nowrap font-medium tracking-normal bg-primary-lighter text-black static`}
                >
                  <TableSortLabel
                    active={sortColumn === column.id}
                    direction={sortColumn === column.id ? sortDirection : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              {actionsRows && (
                <TableCell
                  className={`text-sm capitalize font-bold whitespace-nowrap text-nowrap  pr-2 tracking-normal bg-primary-lighter text-black`}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((row: Record<string, string>, index: number) => (
              <TableRow key={row.id} sx={{ borderColor: "#336CB74D" }}>
                {columns?.map((column: Record<string, string>) => (
                  <TableCell
                    key={column.id}
                    className={`whitespace-now${
                      column.id === "actions"
                        ? "sm:sticky sm:right-0 bg-[#fff] sm:bg-slate-100 sm:border-b-white sm:px-2 p-0 sm:text-center"
                        : "static"
                    }`}
                  >
                    {column?.id?.toLocaleLowerCase().includes("date") ? (
                      dayjs(row[column.id]).format("MM/DD/YYYY hh:mm")
                    ) : column?.id?.toLocaleLowerCase().includes("amount") ? (
                      <span>
                        {row[column.id] ? (
                          <span className="text-sm font-normal">
                            {formatSierraLeoneLeone(Number(row[column.id]))}
                          </span>
                        ) : (
                          "0.00"
                        )}
                      </span>
                    ) : column?.id?.toLocaleLowerCase().includes("link") &&
                      row[column.id] ? (
                      <>
                        {showMap && (
                          <Dialog
                            maxWidth={"lg"}
                            open={showMap}
                            className="flex justify-between items-center w-full"
                          >
                            <div className="w-full h-full relative">
                              <IconButton
                                onClick={() => setShowMap(false)}
                                className="absolute right-0 top-0"
                              >
                                <CloseTwoTone />
                              </IconButton>
                              <div className="w-[80vw] h-[80vh]">
                                <iframe
                                  src={row[column.id]}
                                  title={"Map"}
                                  className="w-full h-full"
                                />
                              </div>
                            </div>
                          </Dialog>
                        )}

                        <Button
                          variant="text"
                          className="text-primary hover:underline text-sm font-normal flex items-center"
                          onClick={() => setShowMap(!showMap)}
                        >
                          View
                        </Button>
                      </>
                    ) : column?.id?.toLocaleLowerCase().includes("status") ? (
                      <Chip
                        label={row[column.id] || "N/A"}
                        color={getStatusColor(row[column.id])}
                      />
                    ) : (
                      <>{row[column.id] || "N/A"}</>
                    )}
                  </TableCell>
                ))}
                {actionsRows && (
                  <TableCell>
                    {actionsRows?.props?.children[index] ?? ""}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {data?.length === 0 ? (
        <div className="m-3 text-center p-2">
          {loading ? "Loading..." : "No data found"}
        </div>
      ) : null}
      {totalRecords && totalRecords !== 0 ? (
        <div className="flex justify-end items-center">
          <div className="flex gap-2 items-center">
            <FormLabel className="text-sm text-black">Page: </FormLabel>
            <Select
              value={page + 1}
              size="small"
              variant="standard"
              onChange={(e: any) => handleChangePage(e, e.target.value - 1)}
            >
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(
                (i) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                )
              )}
            </Select>
          </div>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            showFirstButton={true}
            showLastButton={true}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      ) : null}
    </Card>
  );
}

export default TableList;
